import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  Building2,
  CheckCircle2,
  ChevronRight,
  Compass,
  Filter,
  Layers,
  Loader2,
  Map as MapIcon,
  MessageSquare,
  Network,
  Search,
  Sparkles,
  Tag,
  Target,
  X,
  Zap,
} from 'lucide-react';
import { clsx } from 'clsx';
import { useScenariosStore } from '@/stores/scenarios';
import { marketResearchApi, type MarketClientSummary } from '@/services/api';
import type {
  FoundationQuerySummary,
  KeywordIntent,
  ScenarioDetail,
  ScenarioSummary,
} from '@/services/api';

function formatClientName(client: MarketClientSummary): string {
  if (client.name) return client.name;
  const uid = client.client_uid || '';
  return uid.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const COMPLEXITY_COLORS: Record<string, string> = {
  low: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  medium: 'bg-amber-50 text-amber-700 border-amber-200',
  high: 'bg-orange-50 text-orange-700 border-orange-200',
  very_high: 'bg-rose-50 text-rose-700 border-rose-200',
};

const INTENT_COLORS: Record<string, string> = {
  first_time: 'bg-sky-50 text-sky-700 border-sky-200',
  replacement: 'bg-violet-50 text-violet-700 border-violet-200',
  upgrade: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  gift: 'bg-pink-50 text-pink-700 border-pink-200',
  impulse: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200',
  planned: 'bg-teal-50 text-teal-700 border-teal-200',
  emergency: 'bg-rose-50 text-rose-700 border-rose-200',
};

export function ScenariosPage() {
  const {
    clientId,
    scenarios,
    totalCount,
    facets,
    listLoading,
    listError,
    filters,
    selectedScenarioId,
    detail,
    detailLoading,
    detailError,
    keywordIntents,
    keywordIntentsLoading,
    keywordIntentsError,
    foundationQueries,
    foundationQueriesLoading,
    foundationQueriesError,
    setClient,
    setFilter,
    clearFilters,
    selectScenario,
  } = useScenariosStore();

  const navigate = useNavigate();
  const openInGraph = (scenarioId: string) => {
    if (!clientId) return;
    navigate(
      `/graph?focus=${encodeURIComponent(scenarioId)}&client=${encodeURIComponent(clientId)}`,
    );
  };

  const [clients, setClients] = useState<MarketClientSummary[]>([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [clientsError, setClientsError] = useState<string | null>(null);
  const [clientSearch, setClientSearch] = useState('');

  // Fetch client list on mount — uses the Firestore-backed market endpoint
  // so it works regardless of whether FalkorDB / the cognitive graph is up.
  useEffect(() => {
    setClientsLoading(true);
    marketResearchApi
      .listClients()
      .then((res) => {
        setClients(res.clients || []);
        setClientsError(null);
      })
      .catch((e) => {
        setClientsError(e instanceof Error ? e.message : 'Failed to load clients');
      })
      .finally(() => setClientsLoading(false));
  }, []);

  const filteredClients = useMemo(() => {
    if (!clientSearch.trim()) return clients;
    const q = clientSearch.toLowerCase();
    return clients.filter(
      (c) =>
        formatClientName(c).toLowerCase().includes(q) ||
        (c.client_uid ?? '').toLowerCase().includes(q),
    );
  }, [clients, clientSearch]);

  const selectedClient = clients.find((c) => c.client_uid === clientId);
  const hasActiveFilters =
    !!filters.search || !!filters.purchaseIntent || !!filters.decisionComplexity;

  return (
    <div className="flex gap-0 -mx-6 -mt-6 h-[calc(100vh-4rem)]">
      {/* ── Left: client list ── */}
      <aside className="w-60 flex-shrink-0 bg-white border-r border-slate-200 flex flex-col overflow-hidden">
        <div className="px-4 pt-5 pb-3 border-b border-slate-100">
          <div className="flex items-center gap-2 mb-3">
            <Building2 size={14} className="text-slate-400" />
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
              Clients
            </span>
            {clients.length > 0 && (
              <span className="ml-auto text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full font-medium">
                {clients.length}
              </span>
            )}
          </div>
          {clients.length > 6 && (
            <div className="relative">
              <Search
                size={12}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                type="text"
                value={clientSearch}
                onChange={(e) => setClientSearch(e.target.value)}
                placeholder="Filter clients…"
                className="w-full pl-7 pr-3 py-1.5 text-xs border border-slate-200 rounded-lg bg-slate-50 text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-brand-teal/40 focus:border-brand-teal/50"
              />
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto py-2">
          {clientsLoading && clients.length === 0 && (
            <div className="flex items-center justify-center py-8">
              <Loader2 size={16} className="animate-spin text-slate-300" />
            </div>
          )}
          {clientsError && (
            <p className="mx-4 my-3 text-xs text-rose-600">{clientsError}</p>
          )}
          {!clientsLoading && filteredClients.length === 0 && !clientsError && (
            <p className="px-4 py-6 text-xs text-slate-400 text-center">
              No clients found
            </p>
          )}
          {filteredClients.map((c) => {
            const selected = clientId === c.client_uid;
            const name = formatClientName(c);
            return (
              <button
                key={c.client_uid}
                onClick={() => {
                  void setClient(c.client_uid);
                }}
                className={clsx(
                  'w-full text-left px-4 py-2.5 flex items-center gap-3 transition-all group',
                  selected
                    ? 'bg-brand-teal/8 border-r-2 border-brand-teal'
                    : 'hover:bg-slate-50 border-r-2 border-transparent',
                )}
              >
                <div
                  className={clsx(
                    'w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 text-[10px] font-bold uppercase',
                    selected
                      ? 'bg-brand-teal/20 text-brand-teal-dark'
                      : 'bg-slate-100 text-slate-500 group-hover:bg-slate-200',
                  )}
                >
                  {name.slice(0, 2)}
                </div>
                <div className="min-w-0 flex-1">
                  <p
                    className={clsx(
                      'text-xs font-semibold truncate',
                      selected ? 'text-brand-teal-dark' : 'text-navy-900',
                    )}
                  >
                    {name}
                  </p>
                  <p className="text-[10px] text-slate-400 font-mono truncate">
                    {c.client_uid}
                  </p>
                </div>
                {selected && (
                  <ChevronRight size={12} className="text-brand-teal flex-shrink-0" />
                )}
              </button>
            );
          })}
        </div>
      </aside>

      {/* ── Middle: scenarios list ── */}
      <div className="flex-1 flex flex-col min-w-0 bg-slate-50 overflow-hidden">
        {/* Top bar */}
        <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center gap-3 flex-shrink-0">
          <div className="flex-1 min-w-0">
            {selectedClient ? (
              <div>
                <h1 className="text-lg font-bold text-navy-900 flex items-center gap-2">
                  <Compass size={18} className="text-brand-teal" />
                  Buyer Scenarios
                </h1>
                <p className="text-xs text-slate-400 font-mono truncate">
                  {formatClientName(selectedClient)} · {selectedClient.client_uid}
                </p>
              </div>
            ) : (
              <div>
                <h1 className="text-lg font-bold text-navy-900 flex items-center gap-2">
                  <Compass size={18} className="text-brand-teal" />
                  Buyer Scenarios
                </h1>
                <p className="text-xs text-slate-500">
                  Browse resolved scenarios and their keyword intent linkage
                </p>
              </div>
            )}
          </div>

          {/* Search */}
          {clientId && (
            <div className="relative">
              <Search
                size={13}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                type="text"
                value={filters.search}
                onChange={(e) => setFilter('search', e.target.value)}
                placeholder="Search scenarios…"
                className="pl-8 pr-8 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-brand-teal/40 focus:border-brand-teal/50 w-56"
              />
              {filters.search && (
                <button
                  onClick={() => setFilter('search', '')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X size={12} />
                </button>
              )}
            </div>
          )}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-xs text-slate-500 hover:text-navy-900 flex items-center gap-1"
            >
              <X size={12} />
              Clear
            </button>
          )}
        </div>

        {/* Facet filters */}
        {clientId && (facets.purchase_intent || facets.decision_complexity) && (
          <div className="bg-white border-b border-slate-100 px-6 py-2.5 flex items-center gap-4 flex-wrap flex-shrink-0">
            {facets.purchase_intent && Object.keys(facets.purchase_intent).length > 0 && (
              <FacetGroup
                label="Intent"
                icon={<Target size={11} />}
                facet={facets.purchase_intent}
                active={filters.purchaseIntent}
                onSelect={(v) => setFilter('purchaseIntent', v)}
              />
            )}
            {facets.decision_complexity &&
              Object.keys(facets.decision_complexity).length > 0 && (
                <FacetGroup
                  label="Complexity"
                  icon={<Layers size={11} />}
                  facet={facets.decision_complexity}
                  active={filters.decisionComplexity}
                  onSelect={(v) => setFilter('decisionComplexity', v)}
                />
              )}
          </div>
        )}

        {/* Error */}
        {listError && (
          <div className="mx-6 mt-4 bg-amber-50 border border-amber-200 text-amber-700 text-sm rounded-xl px-4 py-3 flex items-center gap-2">
            <AlertCircle size={16} />
            <span>{listError}</span>
          </div>
        )}

        {/* Empty states */}
        {!clientId && !listLoading && (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <MapIcon size={40} className="mx-auto text-slate-200 mb-4" />
              <p className="text-sm font-medium text-slate-500">
                Select a client from the left
              </p>
              <p className="text-xs text-slate-400 mt-1">
                to browse their resolved buyer scenarios
              </p>
            </div>
          </div>
        )}

        {listLoading && (
          <div className="flex items-center justify-center py-24">
            <Loader2 size={20} className="animate-spin text-slate-400" />
            <span className="ml-3 text-sm text-slate-500">Loading scenarios…</span>
          </div>
        )}

        {!listLoading && clientId && (
          <div className="flex flex-1 min-h-0 overflow-hidden">
            {/* Scenario list */}
            <div className="flex-1 overflow-y-auto p-6 min-w-0">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  {totalCount} scenario{totalCount !== 1 ? 's' : ''}
                  {filters.search && ` matching "${filters.search}"`}
                </span>
                {hasActiveFilters && (
                  <span className="text-[10px] font-mono bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full flex items-center gap-1">
                    <Filter size={8} />
                    filtered
                  </span>
                )}
              </div>

              {scenarios.length === 0 && !listError && (
                <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                  <Compass size={28} className="mx-auto text-slate-200 mb-3" />
                  <p className="text-sm text-slate-500">
                    {hasActiveFilters
                      ? 'No scenarios match the current filters.'
                      : 'No resolved scenarios found for this client yet.'}
                  </p>
                  {hasActiveFilters && (
                    <button
                      onClick={clearFilters}
                      className="mt-2 text-xs text-brand-teal hover:underline"
                    >
                      Clear filters
                    </button>
                  )}
                </div>
              )}

              <div className="grid gap-3 grid-cols-1 xl:grid-cols-2">
                {scenarios.map((s) => (
                  <ScenarioCard
                    key={s.scenario_id}
                    scenario={s}
                    selected={selectedScenarioId === s.scenario_id}
                    onClick={() => {
                      void selectScenario(s.scenario_id);
                    }}
                    onOpenInGraph={openInGraph}
                  />
                ))}
              </div>
            </div>

            {/* Detail pane */}
            {selectedScenarioId && (
              <aside className="w-[460px] flex-shrink-0 bg-white border-l border-slate-200 overflow-y-auto">
                <ScenarioDetailPane
                  detail={detail}
                  loading={detailLoading}
                  error={detailError}
                  keywordIntents={keywordIntents}
                  keywordIntentsLoading={keywordIntentsLoading}
                  keywordIntentsError={keywordIntentsError}
                  foundationQueries={foundationQueries}
                  foundationQueriesLoading={foundationQueriesLoading}
                  foundationQueriesError={foundationQueriesError}
                  onClose={() => {
                    void selectScenario(null);
                  }}
                  onOpenInGraph={openInGraph}
                />
              </aside>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Facet group
// ---------------------------------------------------------------------------

function FacetGroup({
  label,
  icon,
  facet,
  active,
  onSelect,
}: {
  label: string;
  icon: ReactNode;
  facet: Record<string, number>;
  active: string;
  onSelect: (value: string) => void;
}) {
  const entries = Object.entries(facet).sort((a, b) => b[1] - a[1]);
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[10px] font-semibold text-slate-400 uppercase flex items-center gap-1 mr-1">
        {icon}
        {label}
      </span>
      {entries.map(([value, count]) => {
        const isActive = active === value;
        return (
          <button
            key={value}
            onClick={() => onSelect(isActive ? '' : value)}
            className={clsx(
              'text-[10px] font-mono px-2 py-0.5 rounded-full border transition-all',
              isActive
                ? 'bg-brand-teal/15 text-brand-teal-dark border-brand-teal/40'
                : 'bg-slate-50 text-slate-500 border-slate-200 hover:border-slate-300',
            )}
          >
            {value} <span className="opacity-60">· {count}</span>
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Scenario card
// ---------------------------------------------------------------------------

function ScenarioCard({
  scenario,
  selected,
  onClick,
  onOpenInGraph,
}: {
  scenario: ScenarioSummary;
  selected: boolean;
  onClick: () => void;
  onOpenInGraph: (scenarioId: string) => void;
}) {
  const complexityCls =
    COMPLEXITY_COLORS[scenario.decision_complexity] ??
    'bg-slate-50 text-slate-600 border-slate-200';
  const intentCls =
    INTENT_COLORS[scenario.purchase_intent] ??
    'bg-slate-50 text-slate-600 border-slate-200';

  return (
    <div
      onClick={onClick}
      className={clsx(
        'relative text-left bg-white rounded-xl border shadow-sm p-4 transition-all group cursor-pointer',
        selected
          ? 'border-brand-teal shadow-soft ring-2 ring-brand-teal/20'
          : 'border-slate-200 hover:border-brand-teal/40 hover:shadow-soft',
      )}
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onOpenInGraph(scenario.scenario_id);
        }}
        title="Open this scenario in the Cognitive Graph"
        className="absolute top-3 right-3 p-1.5 rounded-lg text-slate-400 hover:text-brand-teal hover:bg-brand-teal/10 transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
      >
        <Network size={13} />
      </button>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-brand-teal/20 to-brand-purple/20 flex items-center justify-center text-brand-teal flex-shrink-0">
          <Target size={16} />
        </div>
        <div className="min-w-0 flex-1 pr-6">
          <h3 className="text-sm font-bold text-navy-900 truncate group-hover:text-brand-teal-dark transition-colors">
            {scenario.title || scenario.scenario_id}
          </h3>
          <p className="text-[10px] font-mono text-slate-400 truncate">
            {scenario.scenario_id}
          </p>
          {scenario.description && (
            <p className="text-xs text-slate-500 mt-1.5 line-clamp-2">
              {scenario.description}
            </p>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 mt-3">
        {scenario.purchase_intent && (
          <span
            className={clsx(
              'text-[9px] font-mono uppercase px-1.5 py-0.5 rounded-full border',
              intentCls,
            )}
          >
            {scenario.purchase_intent}
          </span>
        )}
        {scenario.decision_complexity && (
          <span
            className={clsx(
              'text-[9px] font-mono uppercase px-1.5 py-0.5 rounded-full border',
              complexityCls,
            )}
          >
            {scenario.decision_complexity}
          </span>
        )}
        {scenario.source_persona_id && (
          <span className="text-[9px] bg-slate-50 text-slate-600 border border-slate-200 px-1.5 py-0.5 rounded-full font-mono">
            persona: {scenario.source_persona_id}
          </span>
        )}
        {typeof scenario.confidence_score === 'number' && (
          <span className="text-[9px] bg-slate-50 text-slate-500 border border-slate-200 px-1.5 py-0.5 rounded-full font-mono">
            conf {scenario.confidence_score.toFixed(2)}
          </span>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail pane
// ---------------------------------------------------------------------------

function ScenarioDetailPane({
  detail,
  loading,
  error,
  keywordIntents,
  keywordIntentsLoading,
  keywordIntentsError,
  foundationQueries,
  foundationQueriesLoading,
  foundationQueriesError,
  onClose,
  onOpenInGraph,
}: {
  detail: ScenarioDetail | null;
  loading: boolean;
  error: string | null;
  keywordIntents: KeywordIntent[];
  keywordIntentsLoading: boolean;
  keywordIntentsError: string | null;
  foundationQueries: FoundationQuerySummary[];
  foundationQueriesLoading: boolean;
  foundationQueriesError: string | null;
  onClose: () => void;
  onOpenInGraph: (scenarioId: string) => void;
}) {
  const totalProbedQueries = foundationQueries.filter(
    (q) => !q.needs_probing,
  ).length;
  const totalBrandHits = foundationQueries.reduce(
    (sum, q) => sum + (q.recommended_brand_count || 0),
    0,
  );

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-5 py-4 flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-semibold uppercase text-brand-teal tracking-wide">
            Scenario Detail
          </p>
          {loading && !detail ? (
            <p className="text-sm font-bold text-slate-300">Loading…</p>
          ) : (
            <h2 className="text-base font-bold text-navy-900 leading-snug">
              {detail?.title ?? '—'}
            </h2>
          )}
          {detail?.scenario_id && (
            <p className="text-[10px] font-mono text-slate-400 truncate">
              {detail.scenario_id}
            </p>
          )}
        </div>
        {detail?.scenario_id && (
          <button
            onClick={() => onOpenInGraph(detail.scenario_id)}
            title="Focus this scenario in the Cognitive Graph and expand its linked entities"
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-semibold text-brand-teal-dark bg-brand-teal/10 hover:bg-brand-teal/15 border border-brand-teal/30 rounded-lg transition-colors"
          >
            <Network size={12} />
            Open in graph
          </button>
        )}
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600"
        >
          <X size={14} />
        </button>
      </div>

      {/* Knowledge surface summary bar */}
      {detail && (
        <div className="px-5 py-2.5 bg-slate-50 border-b border-slate-100 flex items-center gap-3 text-[11px]">
          <span className="text-slate-400 uppercase font-semibold tracking-wide text-[9px]">
            Linked knowledge
          </span>
          <KnowledgeStat
            icon={<Sparkles size={11} />}
            label="keyword intents"
            value={keywordIntents.length}
            loading={keywordIntentsLoading}
            tone="teal"
          />
          <KnowledgeStat
            icon={<MessageSquare size={11} />}
            label={
              totalProbedQueries > 0
                ? `FM queries (${totalProbedQueries} probed)`
                : 'FM queries'
            }
            value={foundationQueries.length}
            loading={foundationQueriesLoading}
            tone="indigo"
          />
          {totalBrandHits > 0 && (
            <KnowledgeStat
              icon={<CheckCircle2 size={11} />}
              label="brand mentions"
              value={totalBrandHits}
              loading={false}
              tone="slate"
            />
          )}
        </div>
      )}

      <div className="flex-1 overflow-y-auto">
        {error && (
          <div className="mx-5 mt-4 bg-rose-50 border border-rose-200 text-rose-700 text-xs rounded-lg px-3 py-2 flex items-center gap-2">
            <AlertCircle size={14} />
            {error}
          </div>
        )}
        {loading && !detail && (
          <div className="flex items-center justify-center py-10">
            <Loader2 size={18} className="animate-spin text-slate-400" />
          </div>
        )}

        {detail && (
          <div className="p-5 space-y-5">
            {/* Summary chips */}
            <div className="flex flex-wrap gap-1.5">
              {detail.purchase_intent && (
                <Chip label={`intent · ${detail.purchase_intent}`} tone="teal" />
              )}
              {detail.decision_complexity && (
                <Chip
                  label={`complexity · ${detail.decision_complexity}`}
                  tone="amber"
                />
              )}
              {detail.typical_timeline && (
                <Chip label={`timeline · ${detail.typical_timeline}`} tone="slate" />
              )}
              {detail.resolution_status && (
                <Chip
                  label={`resolution · ${detail.resolution_status}`}
                  tone="indigo"
                />
              )}
            </div>

            {detail.description && (
              <Section title="Description">
                <p className="text-xs text-slate-600 leading-relaxed whitespace-pre-wrap">
                  {detail.description}
                </p>
              </Section>
            )}

            {detail.trigger_event && (
              <Section title="Trigger event" icon={<Zap size={12} />}>
                <p className="text-xs text-slate-600 leading-relaxed">
                  {detail.trigger_event}
                </p>
              </Section>
            )}

            <KeyValueSection title="Buyer profile" data={detail.buyer_profile} />
            <KeyValueSection
              title="Decision factors"
              data={detail.decision_factors}
            />
            <KeyValueSection
              title="Information journey"
              data={detail.information_journey}
            />
            <KeyValueSection
              title="Emotional context"
              data={detail.emotional_context}
            />
            <KeyValueSection
              title="Category context"
              data={detail.category_context}
            />

            {/* Keyword intent linkage */}
            <Section
              title="Keyword intents linked to this scenario"
              icon={<Sparkles size={12} />}
              count={keywordIntents.length}
            >
              {keywordIntentsLoading && (
                <div className="flex items-center gap-2 text-xs text-slate-400 py-2">
                  <Loader2 size={12} className="animate-spin" />
                  Loading keyword intents…
                </div>
              )}
              {keywordIntentsError && (
                <p className="text-xs text-rose-600">{keywordIntentsError}</p>
              )}
              {!keywordIntentsLoading &&
                !keywordIntentsError &&
                keywordIntents.length === 0 && (
                  <p className="text-xs text-slate-400 italic">
                    No keyword intents have been resolved to this scenario yet.
                  </p>
                )}
              <div className="space-y-2">
                {keywordIntents.map((ki) => (
                  <KeywordIntentRow key={ki.id} intent={ki} scenarioId={detail.scenario_id} />
                ))}
              </div>
            </Section>

            {/* Foundation model queries linkage */}
            <Section
              title="Foundation model queries linked to this scenario"
              icon={<MessageSquare size={12} />}
              count={foundationQueries.length}
            >
              {foundationQueriesLoading && (
                <div className="flex items-center gap-2 text-xs text-slate-400 py-2">
                  <Loader2 size={12} className="animate-spin" />
                  Loading foundation queries…
                </div>
              )}
              {foundationQueriesError && (
                <p className="text-xs text-rose-600">{foundationQueriesError}</p>
              )}
              {!foundationQueriesLoading &&
                !foundationQueriesError &&
                foundationQueries.length === 0 && (
                  <p className="text-xs text-slate-400 italic">
                    No foundation model queries have been linked to this scenario yet.
                  </p>
                )}
              <div className="space-y-2">
                {foundationQueries.map((fmq) => (
                  <FoundationQueryRow key={fmq.query_id} query={fmq} />
                ))}
              </div>
            </Section>

            {/* Relations */}
            {detail.relations.length > 0 && (
              <Section title="Graph relations" count={detail.relations.length}>
                <ul className="space-y-1">
                  {detail.relations.slice(0, 20).map((r, idx) => (
                    <li
                      key={`${r.entity_id}-${idx}`}
                      className="text-[11px] text-slate-600 font-mono flex items-center gap-2 bg-slate-50 border border-slate-100 rounded px-2 py-1"
                    >
                      <span className="text-brand-teal">{r.relationship}</span>
                      <span className="text-slate-400">→</span>
                      <span className="text-slate-500">{r.entity_type}</span>
                      <span className="truncate">{r.entity_id}</span>
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {/* Research metadata */}
            {detail.research_metadata?.research_id && (
              <div className="text-[10px] font-mono text-slate-400 border-t border-slate-100 pt-3">
                research_id: {detail.research_metadata.research_id}
                {detail.research_metadata.discovery_source &&
                  ` · ${detail.research_metadata.discovery_source}`}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Section({
  title,
  icon,
  count,
  children,
}: {
  title: string;
  icon?: ReactNode;
  count?: number;
  children: ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-2">
        {icon && <span className="text-slate-400">{icon}</span>}
        <h3 className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
          {title}
        </h3>
        {typeof count === 'number' && (
          <span className="text-[10px] font-mono bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full">
            {count}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

function KeyValueSection({
  title,
  data,
}: {
  title: string;
  data: Record<string, unknown> | undefined | null;
}) {
  const entries = useMemo(() => {
    if (!data) return [] as Array<[string, unknown]>;
    return Object.entries(data).filter(([, v]) => {
      if (v == null) return false;
      if (Array.isArray(v)) return v.length > 0;
      if (typeof v === 'object') return Object.keys(v as object).length > 0;
      if (typeof v === 'string') return v.trim().length > 0;
      return true;
    });
  }, [data]);

  if (entries.length === 0) return null;

  return (
    <Section title={title}>
      <dl className="grid grid-cols-1 gap-1.5">
        {entries.map(([key, value]) => (
          <div
            key={key}
            className="text-xs bg-slate-50 border border-slate-100 rounded-lg px-3 py-2"
          >
            <dt className="text-[10px] font-mono uppercase text-slate-400 tracking-wide">
              {key.replace(/_/g, ' ')}
            </dt>
            <dd className="text-slate-700 mt-0.5 break-words">
              {renderValue(value)}
            </dd>
          </div>
        ))}
      </dl>
    </Section>
  );
}

function renderValue(value: unknown): ReactNode {
  if (value == null) return <span className="text-slate-300">—</span>;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-slate-300">—</span>;
    return (
      <div className="flex flex-wrap gap-1">
        {value.slice(0, 12).map((v, i) => (
          <span
            key={i}
            className="text-[10px] bg-white border border-slate-200 text-slate-600 px-1.5 py-0.5 rounded"
          >
            {typeof v === 'object' ? JSON.stringify(v) : String(v)}
          </span>
        ))}
        {value.length > 12 && (
          <span className="text-[10px] text-slate-400">+{value.length - 12}</span>
        )}
      </div>
    );
  }
  // Object: render as key→value pairs
  const entries = Object.entries(value as Record<string, unknown>);
  return (
    <ul className="space-y-0.5">
      {entries.slice(0, 10).map(([k, v]) => (
        <li key={k} className="text-[11px] text-slate-600">
          <span className="text-slate-400 font-mono">{k}:</span>{' '}
          <span className="break-words">
            {typeof v === 'object' ? JSON.stringify(v) : String(v)}
          </span>
        </li>
      ))}
      {entries.length > 10 && (
        <li className="text-[10px] text-slate-400">+{entries.length - 10} more</li>
      )}
    </ul>
  );
}

function Chip({
  label,
  tone,
}: {
  label: string;
  tone: 'teal' | 'amber' | 'slate' | 'indigo';
}) {
  const cls = {
    teal: 'bg-brand-teal/10 text-brand-teal-dark border-brand-teal/30',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    slate: 'bg-slate-50 text-slate-600 border-slate-200',
    indigo: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  }[tone];
  return (
    <span
      className={clsx(
        'text-[10px] font-mono uppercase px-2 py-0.5 rounded-full border',
        cls,
      )}
    >
      {label}
    </span>
  );
}

function KeywordIntentRow({
  intent,
  scenarioId,
}: {
  intent: KeywordIntent;
  scenarioId: string;
}) {
  const link = intent.scenario_links.find((l) => l.scenario_id === scenarioId);
  const isSignal = intent.entity_type === 'intent_signal';

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3 hover:border-brand-teal/40 transition-colors">
      <div className="flex items-start gap-2">
        <div
          className={clsx(
            'w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0',
            isSignal
              ? 'bg-indigo-50 text-indigo-500'
              : 'bg-brand-teal/10 text-brand-teal',
          )}
        >
          <Tag size={12} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <p className="text-xs font-semibold text-navy-900 truncate">
              {intent.canonical_intent || intent.name}
            </p>
            <span
              className={clsx(
                'text-[9px] font-mono uppercase px-1 py-0.5 rounded border',
                isSignal
                  ? 'bg-indigo-50 text-indigo-600 border-indigo-200'
                  : 'bg-brand-teal/10 text-brand-teal-dark border-brand-teal/30',
              )}
            >
              {intent.entity_type}
            </span>
            {link && (
              <span className="text-[9px] font-mono text-slate-500 bg-slate-50 border border-slate-200 px-1 py-0.5 rounded">
                sim {link.similarity.toFixed(2)}
                {link.action ? ` · ${link.action}` : ''}
              </span>
            )}
          </div>
          {intent.keywords.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {intent.keywords.slice(0, 8).map((kw, i) => (
                <span
                  key={`${kw}-${i}`}
                  className="text-[10px] bg-slate-50 text-slate-600 border border-slate-100 px-1.5 py-0.5 rounded font-mono"
                >
                  {kw}
                </span>
              ))}
              {intent.keywords.length > 8 && (
                <span className="text-[10px] text-slate-400">
                  +{intent.keywords.length - 8}
                </span>
              )}
            </div>
          )}
          <div className="flex items-center gap-2 mt-1.5 text-[10px] text-slate-400 font-mono">
            {typeof intent.keyword_count === 'number' && (
              <span>{intent.keyword_count} kw</span>
            )}
            {typeof intent.total_views === 'number' && (
              <span>· {intent.total_views.toLocaleString()} views</span>
            )}
            {typeof intent.total_visitors === 'number' && (
              <span>· {intent.total_visitors.toLocaleString()} visitors</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Knowledge stat chip (header summary bar)
// ---------------------------------------------------------------------------

function KnowledgeStat({
  icon,
  label,
  value,
  loading,
  tone,
}: {
  icon: ReactNode;
  label: string;
  value: number;
  loading: boolean;
  tone: 'teal' | 'indigo' | 'slate';
}) {
  const cls = {
    teal: 'text-brand-teal-dark bg-brand-teal/10 border-brand-teal/30',
    indigo: 'text-indigo-700 bg-indigo-50 border-indigo-200',
    slate: 'text-slate-600 bg-white border-slate-200',
  }[tone];
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-medium',
        cls,
      )}
    >
      {icon}
      {loading ? (
        <Loader2 size={10} className="animate-spin" />
      ) : (
        <span className="font-mono">{value}</span>
      )}
      <span className="text-[9px] opacity-70 uppercase">{label}</span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Foundation query row
// ---------------------------------------------------------------------------

function FoundationQueryRow({ query }: { query: FoundationQuerySummary }) {
  const probed = !query.needs_probing;
  const iconCls = probed
    ? 'bg-emerald-50 text-emerald-600'
    : 'bg-slate-50 text-slate-400';

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3 hover:border-brand-teal/40 transition-colors">
      <div className="flex items-start gap-2">
        <div
          className={clsx(
            'w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0',
            iconCls,
          )}
        >
          <MessageSquare size={12} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <p className="text-xs font-semibold text-navy-900 line-clamp-2">
              {query.canonical_query || query.query_id}
            </p>
          </div>
          <div className="flex items-center gap-1 flex-wrap mt-1.5">
            {query.query_type && (
              <span className="text-[9px] font-mono uppercase px-1 py-0.5 rounded border bg-indigo-50 text-indigo-600 border-indigo-200">
                {query.query_type}
              </span>
            )}
            {query.intent_category && (
              <span className="text-[9px] font-mono uppercase px-1 py-0.5 rounded border bg-slate-50 text-slate-500 border-slate-200">
                {query.intent_category}
              </span>
            )}
            {query.complexity && (
              <span className="text-[9px] font-mono uppercase px-1 py-0.5 rounded border bg-slate-50 text-slate-500 border-slate-200">
                {query.complexity}
              </span>
            )}
            <span
              className={clsx(
                'text-[9px] font-mono uppercase px-1 py-0.5 rounded border inline-flex items-center gap-0.5',
                probed
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                  : 'bg-slate-50 text-slate-500 border-slate-200',
              )}
            >
              {probed && <CheckCircle2 size={8} />}
              {probed ? 'probed' : 'unprobed'}
            </span>
          </div>
          {probed && (
            <div className="flex items-center gap-2 mt-1.5 text-[10px] text-slate-400 font-mono">
              {query.model_response_count > 0 && (
                <span>{query.model_response_count} responses</span>
              )}
              {query.key_insight_count > 0 && (
                <span>· {query.key_insight_count} insights</span>
              )}
              {query.recommended_brand_count > 0 && (
                <span>· {query.recommended_brand_count} brands</span>
              )}
              {query.recommended_product_count > 0 && (
                <span>· {query.recommended_product_count} products</span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
